import { useCallback, useEffect, useState } from "react";
import api, { money, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, Landmark } from "lucide-react";
import { Attachments } from "@/components/Attachments";

const todayStr = () => new Date().toISOString().slice(0, 10);
export const CHEQUE_TONE = { pending: "secondary", cleared: "default", bounced: "destructive", cancelled: "outline" };

export function ChequeDialog({ order, onClose, onChanged }) {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ cheque_no: "", bank: "", cheque_date: todayStr(), amount: "", notes: "" });

  const load = useCallback(() => api.get(`/admin/orders/${order.id}/cheques`).then((r) => setItems(r.data)), [order]);
  useEffect(() => { if (order) { load(); setForm((f) => ({ ...f, amount: String(order.balance_due ?? "") })); } }, [order, load]);

  const act = async (fn, ok) => {
    setSaving(true);
    try {
      await fn();
      toast.success(ok);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };
  const save = () => act(() => api.post(`/admin/orders/${order.id}/cheques`, { ...form, amount: Number(form.amount) }).then(() => setForm({ cheque_no: "", bank: "", cheque_date: todayStr(), amount: "", notes: "" })), "Cheque recorded");
  const clear = (c) => act(() => api.post(`/admin/cheques/${c.id}/clear`, { date: todayStr() }), "Cheque cleared - receipt posted");
  const bounce = (c) => { const reason = window.prompt("Bounce reason (e.g. insufficient funds)"); if (reason === null) return; act(() => api.post(`/admin/cheques/${c.id}/bounce`, { reason }), "Cheque marked bounced"); };
  const cancel = (c) => act(() => api.post(`/admin/cheques/${c.id}/cancel`, {}), "Cheque cancelled");

  return (
    <Dialog open={!!order} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="cheque-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Landmark className="w-4 h-4 text-primary" /> Post-dated cheques · {order?.invoice_no}</DialogTitle></DialogHeader>
        {!items ? <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-primary" /></div> : (
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">Open balance <b className="text-foreground">{money(order?.balance_due)}</b>. A cheque counts as paid only when you mark it cleared - that posts the receipt and releases commission.</p>
            {items.length > 0 && (
              <div className="space-y-1.5">
                {items.map((c) => (
                  <div key={c.id} className="flex items-center justify-between gap-2 text-sm border border-border/60 rounded-md px-3 py-2" data-testid={`cheque-row-${c.id}`}>
                    <div className="min-w-0">
                      <p className="font-medium">#{c.cheque_no} · {money(c.amount)} <Badge variant={CHEQUE_TONE[c.status]} className="ml-1 capitalize">{c.status}</Badge></p>
                      <p className="text-xs text-muted-foreground truncate">{c.bank || "—"} · dated {c.cheque_date}{c.status === "pending" && (c.matured ? " · matured" : ` · in ${c.days_to_maturity}d`)}{c.bounce_reason ? ` · ${c.bounce_reason}` : ""}</p>
                    </div>
                    {c.status === "pending" && (
                      <div className="flex gap-1 shrink-0">
                        <Button size="sm" variant="default" disabled={saving} onClick={() => clear(c)} data-testid={`clear-cheque-${c.id}`}>Cleared</Button>
                        <Button size="sm" variant="destructive" disabled={saving} onClick={() => bounce(c)} data-testid={`bounce-cheque-${c.id}`}>Bounced</Button>
                      </div>
                    )}
                    {c.status === "bounced" && user?.role === "admin" && <Button size="sm" variant="ghost" disabled={saving} onClick={() => cancel(c)} data-testid={`cancel-cheque-${c.id}`}>Write off</Button>}
                  </div>
                ))}
                {items.map((c) => (
                  <div key={`att-${c.id}`} className="pl-1">
                    <Attachments entityType="cheque" entityId={c.id} kinds={["cheque_scan", "other"]} compact />
                  </div>
                ))}
              </div>
            )}
            {order?.balance_due > 0 && (
              <div className="space-y-3 border-t border-border pt-3">
                <Label className="text-xs">Record a cheque</Label>
                <div className="grid grid-cols-2 gap-3">
                  <F label="Cheque no."><Input value={form.cheque_no} onChange={(e) => setForm({ ...form, cheque_no: e.target.value })} data-testid="cheque-no-input" /></F>
                  <F label="Bank"><Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} data-testid="cheque-bank-input" /></F>
                  <F label="Cheque date"><Input type="date" value={form.cheque_date} onChange={(e) => setForm({ ...form, cheque_date: e.target.value })} data-testid="cheque-date-input" /></F>
                  <F label="Amount (AED)"><Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} data-testid="cheque-amount-input" /></F>
                </div>
              </div>
            )}
          </div>
        )}
        <DialogFooter>
          {order?.balance_due > 0 && <Button onClick={save} disabled={saving || !form.cheque_no || !form.amount} data-testid="save-cheque-button">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Record cheque"}</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function F({ label, children }) {
  return <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{children}</div>;
}
